import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import type { TelemetryApi, TelemetryProperties } from '@unified-governance/shared';

interface Dimensions {
  app?: string;
  remoteName?: string;
}

export interface ShellTelemetry {
  api: TelemetryApi;
  forRemote(remoteName: string, basePath: string): TelemetryApi;
  event(name: string, properties?: TelemetryProperties): void;
  setRoute(route: string): void;
}

const createNoopApi = (): TelemetryApi => ({
  trackEvent: () => undefined,
  trackPageView: () => undefined,
  trackException: () => undefined,
  trackMetric: () => undefined,
  startTrackPage: () => undefined,
  stopTrackPage: () => undefined,
});

export function createShellTelemetry(connectionString: string | undefined): ShellTelemetry {
  let currentRoute = '';

  if (!connectionString) {
    const api = createNoopApi();
    return {
      api,
      forRemote: () => api,
      event: () => undefined,
      setRoute: (route: string) => {
        currentRoute = route;
      },
    };
  }

  const appInsights = new ApplicationInsights({
    config: {
      connectionString,
      enableAutoRouteTracking: true,
      distributedTracingMode: 2,
    },
  });
  appInsights.loadAppInsights();

  const stamp = (
    properties: Record<string, unknown> | undefined,
    dimensions?: Dimensions
  ): Record<string, unknown> => ({
    route: currentRoute,
    ...(dimensions?.app ? { app: dimensions.app } : {}),
    ...(dimensions?.remoteName ? { remoteName: dimensions.remoteName } : {}),
    ...(properties ?? {}),
  });

  const toStringMap = (properties: Record<string, unknown>): Record<string, string> => {
    const result: Record<string, string> = {};
    for (const [key, value] of Object.entries(properties)) {
      result[key] = typeof value === 'string' ? value : String(value);
    }
    return result;
  };

  const makeApi = (dimensions?: Dimensions): TelemetryApi => ({
    trackEvent: (name, properties) =>
      appInsights.trackEvent({ name }, stamp(properties, dimensions)),
    trackPageView: (view) =>
      appInsights.trackPageView({
        name: view.name,
        uri: view.uri,
        properties: stamp(view.properties, dimensions),
      }),
    trackException: (error, properties) =>
      appInsights.trackException(
        { exception: error instanceof Error ? error : new Error(String(error)) },
        stamp(properties, dimensions)
      ),
    trackMetric: (name, value, properties) =>
      appInsights.trackMetric({ name, average: value }, stamp(properties, dimensions)),
    startTrackPage: (name) => appInsights.startTrackPage(name),
    stopTrackPage: (name, properties) =>
      appInsights.stopTrackPage(name, undefined, toStringMap(stamp(properties, dimensions))),
  });

  const api = makeApi();

  return {
    api,
    forRemote: (remoteName, basePath) => makeApi({ app: basePath, remoteName }),
    event: (name, properties) => api.trackEvent(name, properties),
    setRoute: (route: string) => {
      currentRoute = route;
    },
  };
}
